import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  companyInternalReceipts,
  type InternalReceiptItem,
} from '../../api/company/client'
import { Loader2, Plus, Archive, Trash2, Copy, QrCode } from 'lucide-react'

const PAGE_SIZE = 20
const STATUS_OPTIONS = [
  { value: '', labelKey: 'company.internalReceipts.allStatuses' },
  { value: 'active', labelKey: 'company.internalReceipts.active' },
  { value: 'archived', labelKey: 'company.internalReceipts.archived' },
  { value: 'deleted', labelKey: 'company.internalReceipts.deleted' },
]

export default function CompanyInternalReceipts() {
  const { t } = useTranslation('portal')
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(0)
  const [createOpen, setCreateOpen] = useState(false)
  const [createAmount, setCreateAmount] = useState('')
  const [createReceiptNumber, setCreateReceiptNumber] = useState('')
  const [createDateTime, setCreateDateTime] = useState('')
  const [createTaxAmount, setCreateTaxAmount] = useState('')
  const [createRefInvoiceNumber, setCreateRefInvoiceNumber] = useState('')
  const [createRefInvoiceDate, setCreateRefInvoiceDate] = useState('')
  const [createInternalNotes, setCreateInternalNotes] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const listQuery = useQuery({
    queryKey: ['company', 'internal-receipts', statusFilter, page],
    queryFn: async () =>
      (
        await companyInternalReceipts.list({
          status: statusFilter || undefined,
          skip: page * PAGE_SIZE,
          limit: PAGE_SIZE,
          include_qr: false,
        })
      ).data,
  })

  const detailQuery = useQuery({
    queryKey: ['company', 'internal-receipt', detailId],
    queryFn: () =>
      companyInternalReceipts.get(detailId!, { include_qr: true }).then((r) => r.data),
    enabled: !!detailId,
  })

  const createMutation = useMutation({
    mutationFn: (data: {
      amount: number | string
      receipt_number?: string | null
      receipt_datetime?: string | null
      tax_amount?: number | string | null
      reference_invoice_number?: string | null
      reference_invoice_date?: string | null
      internal_notes?: string | null
    }) => companyInternalReceipts.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'internal-receipts'] })
      setCreateOpen(false)
      setCreateAmount('')
      setCreateReceiptNumber('')
      setCreateDateTime('')
      setCreateTaxAmount('')
      setCreateRefInvoiceNumber('')
      setCreateRefInvoiceDate('')
      setCreateInternalNotes('')
      setCreateError(null)
    },
    onError: (err: { response?: { data?: { detail?: { error?: string; message?: string } } } }) => {
      const detail = err.response?.data?.detail
      if (detail?.error === 'RECEIPT_NUMBER_EXISTS') {
        setCreateError(t('company.internalReceipts.receiptNumberExists'))
      } else {
        setCreateError(detail?.message ?? 'Error')
      }
    },
  })

  const archiveMutation = useMutation({
    mutationFn: (receiptId: string) => companyInternalReceipts.archive(receiptId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'internal-receipts'] })
      if (detailId) queryClient.invalidateQueries({ queryKey: ['company', 'internal-receipt', detailId] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (receiptId: string) => companyInternalReceipts.delete(receiptId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'internal-receipts'] })
      setDetailId(null)
    },
  })

  const items = (listQuery.data?.items ?? []) as InternalReceiptItem[]
  const total = listQuery.data?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  const handleCreate = () => {
    const amount = parseFloat(createAmount)
    if (Number.isNaN(amount) || amount <= 0) {
      setCreateError(t('company.internalReceipts.amount') + ' ')
      return
    }
    setCreateError(null)
    createMutation.mutate({
      amount,
      receipt_number: createReceiptNumber.trim() || null,
      receipt_datetime: createDateTime.trim() || null,
      tax_amount: createTaxAmount.trim() ? parseFloat(createTaxAmount) : null,
      reference_invoice_number: createRefInvoiceNumber.trim() || null,
      reference_invoice_date: createRefInvoiceDate.trim() || null,
      internal_notes: createInternalNotes.trim() || null,
    })
  }

  const copyUrl = (id: string, url: string) => {
    navigator.clipboard.writeText(url)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const statusLabel = (status: string) => {
    if (status === 'active') return t('company.internalReceipts.active')
    if (status === 'archived') return t('company.internalReceipts.archived')
    if (status === 'deleted') return t('company.internalReceipts.deleted')
    return status
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">
        {t('company.internalReceipts.title')}
      </h1>
      <p className="text-gray-500 text-sm mb-6">{t('company.internalReceipts.hint')}</p>

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(0)
          }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-gray-900 text-sm"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="rounded-lg bg-blue-600 hover:bg-blue-700 px-4 py-2 text-white font-medium flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> {t('company.internalReceipts.create')}
        </button>
      </div>

      {createOpen && (
        <div className="rounded-xl bg-white border border-gray-200 p-6 shadow-sm mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('company.internalReceipts.create')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('company.internalReceipts.amount')}</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={createAmount}
                onChange={(e) => setCreateAmount(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('company.internalReceipts.receiptNumber')}</label>
              <input
                type="text"
                value={createReceiptNumber}
                onChange={(e) => setCreateReceiptNumber(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                placeholder="e.g. 25-330-000001"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('company.internalReceipts.receiptDateTime')}</label>
              <input
                type="text"
                value={createDateTime}
                onChange={(e) => setCreateDateTime(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                placeholder="2025-02-13 12:00"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('company.internalReceipts.taxAmount')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={createTaxAmount}
                onChange={(e) => setCreateTaxAmount(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('company.internalReceipts.refInvoiceNumber')}</label>
              <input
                type="text"
                value={createRefInvoiceNumber}
                onChange={(e) => setCreateRefInvoiceNumber(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                placeholder={t('company.internalReceipts.refInvoiceNumberPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('company.internalReceipts.refInvoiceDate')}</label>
              <input
                type="date"
                value={createRefInvoiceDate}
                onChange={(e) => setCreateRefInvoiceDate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm text-gray-600 mb-1">{t('company.internalReceipts.internalNotes')}</label>
              <textarea
                value={createInternalNotes}
                onChange={(e) => setCreateInternalNotes(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                rows={2}
                maxLength={1000}
                placeholder={t('company.internalReceipts.internalNotesPlaceholder')}
              />
            </div>
          </div>
          {createError && <p className="mt-2 text-sm text-red-600">{createError}</p>}
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={handleCreate}
              disabled={createMutation.isPending}
              className="rounded-lg bg-blue-600 hover:bg-blue-700 px-4 py-2 text-white font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('company.internalReceipts.create')}
            </button>
            <button
              type="button"
              onClick={() => {
                setCreateOpen(false)
                setCreateError(null)
              }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 font-medium"
            >
              {t('company.internalReceipts.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
        {listQuery.isLoading ? (
          <div className="p-8 flex items-center justify-center text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : !items.length ? (
          <p className="p-6 text-gray-500 text-sm">{t('company.internalReceipts.noReceipts')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left p-3">{t('company.internalReceipts.receiptNumber')}</th>
                  <th className="text-left p-3">{t('company.internalReceipts.amount')}</th>
                  <th className="text-left p-3">{t('company.internalReceipts.status')}</th>
                  <th className="text-left p-3">{t('company.internalReceipts.created')}</th>
                  <th className="text-left p-3 w-32" />
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-3">
                      <span className="font-mono text-gray-800">{r.receipt_number}</span>
                      {r.reference_invoice_number && (
                        <span className="block text-xs text-gray-400 mt-0.5" title={t('company.internalReceipts.refInvoiceNumber')}>
                          {t('company.internalReceipts.refLabel')}: {r.reference_invoice_number}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-gray-700">
                      {r.amount} {r.currency}
                    </td>
                    <td className="p-3">
                      <span
                        className={
                          r.is_paid
                            ? 'text-green-600'
                            : r.status === 'active'
                              ? 'text-blue-600'
                              : 'text-gray-600'
                        }
                      >
                        {r.is_paid ? t('company.internalReceipts.alreadyPaid') : statusLabel(r.status)}
                      </span>
                    </td>
                    <td className="p-3 text-gray-600">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                    <td className="p-3 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setDetailId(detailId === r.id ? null : r.id)}
                        className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        title={t('company.internalReceipts.qrCode')}
                      >
                        <QrCode className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => copyUrl(r.id, r.pay_url)}
                        className="text-gray-600 hover:text-gray-700 flex items-center gap-1"
                        title={t('company.internalReceipts.copyUrl')}
                      >
                        <Copy className="w-4 h-4" />
                        {copiedId === r.id && <span className="text-xs text-green-600">{t('company.internalReceipts.copied')}</span>}
                      </button>
                      {!r.is_paid && r.status === 'active' && (
                        <>
                          <button
                            type="button"
                            onClick={() => window.confirm(t('company.internalReceipts.archiveConfirm')) && archiveMutation.mutate(r.id)}
                            disabled={archiveMutation.isPending}
                            className="text-amber-600 hover:text-amber-700 flex items-center gap-1 disabled:opacity-50"
                            title={t('company.internalReceipts.archive')}
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => window.confirm(t('company.internalReceipts.deleteConfirm')) && deleteMutation.mutate(r.id)}
                            disabled={deleteMutation.isPending}
                            className="text-red-600 hover:text-red-700 flex items-center gap-1 disabled:opacity-50"
                            title={t('company.internalReceipts.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
            <span className="text-sm text-gray-600">
              {t('company.internalReceipts.totalLabel')}: {total}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
              >
                {t('company.internalReceipts.previous')}
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
              >
                {t('company.internalReceipts.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {detailId && detailQuery.data && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {detailQuery.data.receipt_number} — {detailQuery.data.amount} {detailQuery.data.currency}
            </h3>
            {(detailQuery.data.reference_invoice_number || detailQuery.data.reference_invoice_date || detailQuery.data.internal_notes) && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                {detailQuery.data.reference_invoice_number && (
                  <p className="text-gray-700">
                    <span className="text-gray-500">{t('company.internalReceipts.refInvoiceNumber')}:</span>{' '}
                    {detailQuery.data.reference_invoice_number}
                  </p>
                )}
                {detailQuery.data.reference_invoice_date && (
                  <p className="text-gray-700">
                    <span className="text-gray-500">{t('company.internalReceipts.refInvoiceDate')}:</span>{' '}
                    {new Date(detailQuery.data.reference_invoice_date).toLocaleDateString()}
                  </p>
                )}
                {detailQuery.data.internal_notes && (
                  <p className="text-gray-700">
                    <span className="text-gray-500">{t('company.internalReceipts.internalNotes')}:</span>{' '}
                    {detailQuery.data.internal_notes}
                  </p>
                )}
              </div>
            )}
            <p className="text-sm text-gray-600 mb-2">{t('company.internalReceipts.payUrl')}</p>
            <div className="flex gap-2 mb-4">
              <input
                readOnly
                value={detailQuery.data.pay_url}
                className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm font-mono bg-gray-50"
              />
              <button
                type="button"
                onClick={() => copyUrl(detailQuery.data!.id, detailQuery.data!.pay_url)}
                className="rounded-lg bg-gray-100 hover:bg-gray-200 px-4 py-2 text-sm font-medium"
              >
                {t('company.internalReceipts.copyUrl')}
              </button>
            </div>
            {detailQuery.data.qr_base64 && (
              <div className="flex justify-center p-4 bg-gray-50 rounded-lg mb-4">
                <img
                  src={`data:image/png;base64,${detailQuery.data.qr_base64}`}
                  alt="QR"
                  className="w-48 h-48 object-contain"
                />
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setDetailId(null)}
                className="rounded-lg bg-blue-600 hover:bg-blue-700 px-4 py-2 text-white font-medium"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
